import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { assetsApi } from '../../lib/assetsApi';
import { useNotification } from '../../contexts/NotificationContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTestTube, FiCheck, FiX, FiLoader, FiFolder, FiFile, FiRefreshCw, FiUpload, FiFolderPlus, FiAlertTriangle } = FiIcons;

export default function DropboxTester() {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [uploading, setUploading] = useState(false);
  const { showSuccess, showError, showInfo } = useNotification();

  useEffect(() => {
    testConnection();
  }, []);

  const testConnection = async () => {
    setTesting(true);
    try {
      const result = await assetsApi.healthCheck();
      setConnectionStatus(result);
      if (result.ok) {
        showSuccess('Connected to server successfully');
      } else {
        showError('Failed to connect: ' + result.error);
      }
    } catch (error) {
      setConnectionStatus({ success: false, error: error.message });
      showError('Connection test failed: ' + error.message);
    } finally {
      setTesting(false);
    }
  };

  const loadFiles = async () => {
    setLoadingFiles(true);
    try {
      const result = await assetsApi.listAssets();
      setFiles(result.assets || []);
    } catch (error) {
      showError('Failed to load files: ' + error.message);
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  const initializeFolders = async () => {
    try {
      showInfo('Initializing Dropbox folders...');
      const result = await assetsApi.initializeStorage();
      if (result.success) {
        showSuccess('Dropbox folders initialized successfully');
        loadFiles();
      } else {
        showError('Failed to initialize folders: ' + result.error);
      }
    } catch (error) {
      showError('Failed to initialize folders: ' + error.message);
    }
  };

  const handleFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;
    
    setUploading(true);
    setUploadResults([]);
    
    try {
      for (const file of selectedFiles) {
        const result = await assetsApi.uploadAsset(file, 'test');
        setUploadResults(prev => [...prev, { 
          name: file.name, 
          status: 'success', 
          result: result,
          path: result.dropbox_path 
        }]);
      }
      showSuccess(`${selectedFiles.length} file(s) uploaded successfully`);
      loadFiles();
    } catch (error) {
      setUploadResults(prev => [...prev, { 
        name: file.name, 
        status: 'failed', 
        error: error.message 
      }]);
      showError('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const testAssetUrl = async (path) => {
    try {
      const url = await assetsApi.getAssetUrl(path);
      showSuccess('Got temporary URL: ' + url.substring(0, 50) + '...');
    } catch (error) {
      showError('Failed to get asset URL: ' + error.message);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dropbox API Tester</h1>
        <p className="text-gray-600">Test server-side Dropbox API operations</p>
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center">
            <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-yellow-600 mr-2" />
            <p className="text-sm text-yellow-800">
              <strong>Security Note:</strong> All Dropbox operations now go through secure server endpoints. 
              No client-side tokens are exposed.
            </p>
          </div>
        </div>
      </div>

      {/* Connection Status */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Server Connection Status</h2>
          <motion.button
            whileHover={{scale: 1.02}}
            whileTap={{scale: 0.98}}
            onClick={testConnection}
            disabled={testing}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {testing ? (
              <SafeIcon icon={FiLoader} className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <SafeIcon icon={FiTestTube} className="w-4 h-4 mr-2" />
            )}
            Test Connection
          </motion.button>
        </div>
        {connectionStatus && (
          <div className={`p-4 rounded-lg border-2 ${
            connectionStatus.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center">
              <SafeIcon icon={connectionStatus.ok ? FiCheck : FiX} className={`w-5 h-5 mr-3 ${
                connectionStatus.ok ? 'text-green-600' : 'text-red-600'
              }`} />
              <div>
                <p className="font-medium text-gray-900">
                  {connectionStatus.ok ? 'Connected to Server' : 'Connection Failed'}
                </p>
                {connectionStatus.runtime && (
                  <p className="text-sm text-gray-600">
                    Runtime: {connectionStatus.runtime}
                  </p>
                )}
                {connectionStatus.error && (
                  <p className="text-sm text-red-600">{connectionStatus.error}</p>
                )}
              </div>
            </div>
          </div>
        )}
        {connectionStatus?.ok && (
          <motion.button
            whileHover={{scale: 1.02}}
            whileTap={{scale: 0.98}}
            onClick={initializeFolders}
            className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <SafeIcon icon={FiFolderPlus} className="w-4 h-4 mr-2" />
            Initialize Storage
          </motion.button>
        )}
      </div>

      {/* File Upload Test */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Upload Test</h2>
        <div className="text-center">
          <input 
            type="file" 
            multiple 
            onChange={handleFileUpload} 
            disabled={uploading} 
            className="hidden" 
            id="dropbox-upload" 
          />
          <label 
            htmlFor="dropbox-upload" 
            className={`inline-flex items-center px-6 py-3 rounded-lg font-medium transition-colors cursor-pointer ${
              uploading 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {uploading ? (
              <>
                <SafeIcon icon={FiLoader} className="w-5 h-5 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <SafeIcon icon={FiUpload} className="w-5 h-5 mr-2" />
                Choose Files to Upload
              </>
            )}
          </label>
        </div>
        {uploadResults.length > 0 && (
          <div className="mt-4 space-y-2">
            {uploadResults.map((result, index) => (
              <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${
                result.status === 'success' ? 'bg-green-50' : 'bg-red-50'
              }`}>
                <span className="font-medium">{result.name}</span>
                <SafeIcon icon={result.status === 'success' ? FiCheck : FiX} className={`w-4 h-4 ${
                  result.status === 'success' ? 'text-green-600' : 'text-red-600'
                }`} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Files List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Files in Dropbox ({files.length})
          </h2>
          <motion.button
            whileHover={{scale: 1.02}}
            whileTap={{scale: 0.98}}
            onClick={loadFiles}
            disabled={loadingFiles}
            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            {loadingFiles ? (
              <SafeIcon icon={FiLoader} className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
            )}
            Refresh
          </motion.button>
        </div>
        {loadingFiles ? (
          <div className="text-center py-8">
            <SafeIcon icon={FiLoader} className="w-8 h-8 animate-spin text-blue-500 mx-auto" />
            <p className="mt-2 text-gray-600">Loading files...</p>
          </div>
        ) : files.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <SafeIcon icon={FiFolder} className="w-12 h-12 mx-auto mb-4" />
            <p>No files found in Dropbox</p>
            <p className="text-sm mt-2">Try initializing folders first</p>
          </div>
        ) : (
          <div className="space-y-2">
            {files.map((file, index) => (
              <motion.div
                key={file.id}
                initial={{opacity: 0, x: -20}}
                animate={{opacity: 1, x: 0}}
                transition={{delay: index * 0.05}}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center flex-1">
                  <SafeIcon icon={FiFile} className="w-5 h-5 text-gray-500 mr-3" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{file.name}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="capitalize">{file.kind}</span>
                      <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                      <span>{new Date(file.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <motion.button
                  whileHover={{scale: 1.1}}
                  whileTap={{scale: 0.9}}
                  onClick={() => testAssetUrl(file.dropbox_path)}
                  className="p-2 text-blue-500 hover:text-blue-700 transition-colors mr-2"
                  title="Test temporary URL"
                >
                  <SafeIcon icon={FiTestTube} className="w-4 h-4" />
                </motion.button>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}